'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Network,
  Building2,
  HeartPulse,
  ShoppingBag,
  Truck,
  ShieldCheck,
} from 'lucide-react';

/* ==================================================================
   Per-card illustrations (simple SVG, tinted by the card accent)
   ================================================================== */

function NetworkIllust({ color }) {
  return (
    <svg viewBox="0 0 280 160" className="h-full w-full" aria-hidden>
      <line x1="60" y1="36" x2="140" y2="80" stroke={color} strokeWidth="1.5" opacity="0.45" />
      <line x1="60" y1="124" x2="140" y2="80" stroke={color} strokeWidth="1.5" opacity="0.45" />
      <line x1="140" y1="80" x2="220" y2="46" stroke={color} strokeWidth="1.5" opacity="0.45" />
      <line x1="140" y1="80" x2="220" y2="114" stroke={color} strokeWidth="1.5" opacity="0.45" />
      <line x1="60" y1="36" x2="220" y2="46" stroke={color} strokeWidth="1.5" opacity="0.25" strokeDasharray="3 4" />
      <circle cx="60" cy="36" r="10" fill={color} />
      <circle cx="60" cy="124" r="10" fill={color} />
      <circle cx="140" cy="80" r="16" fill={color} />
      <circle cx="140" cy="80" r="5" fill="#ffffff" />
      <circle cx="220" cy="46" r="10" fill={color} />
      <circle cx="220" cy="114" r="10" fill={color} />
    </svg>
  );
}

function ChartIllust({ color }) {
  const bars = [62, 90, 74, 118, 96, 138];
  return (
    <svg viewBox="0 0 280 160" className="h-full w-full" aria-hidden>
      {bars.map((h, i) => (
        <rect
          key={i}
          x={28 + i * 38}
          y={150 - h}
          width="24"
          height={h}
          rx="5"
          fill={color}
          opacity={0.55 + i * 0.07}
        />
      ))}
      <line x1="20" y1="150" x2="260" y2="150" stroke={color} strokeWidth="1" opacity="0.3" />
    </svg>
  );
}

function PulseIllust({ color }) {
  return (
    <svg viewBox="0 0 280 160" className="h-full w-full" aria-hidden>
      <path
        d="M 16 80 L 70 80 L 86 80 L 96 44 L 108 122 L 120 30 L 132 110 L 142 80 L 180 80 L 200 80 L 214 50 L 228 116 L 240 80 L 268 80"
        stroke={color}
        strokeWidth="2.8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="120" cy="30" r="5" fill={color} />
      <circle cx="228" cy="116" r="5" fill={color} />
    </svg>
  );
}

function RetailIllust({ color }) {
  return (
    <svg viewBox="0 0 280 160" className="h-full w-full" aria-hidden>
      {/* Bag handle */}
      <path
        d="M 96 64 L 96 42 Q 96 28 110 28 L 168 28 Q 182 28 182 42 L 182 64"
        stroke={color}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
      {/* Bag body */}
      <rect x="76" y="62" width="124" height="80" rx="8" fill={color} opacity="0.9" />
      {/* Bag highlight */}
      <rect x="76" y="62" width="124" height="14" rx="8" fill="#ffffff" opacity="0.18" />
      {/* Price tag */}
      <g transform="rotate(15 226 50)">
        <rect x="208" y="34" width="40" height="28" rx="4" fill={color} />
        <circle cx="216" cy="42" r="3" fill="#ffffff" />
        <text x="226" y="54" fontSize="10" fontWeight="700" textAnchor="middle" fill="#ffffff">SALE</text>
      </g>
    </svg>
  );
}

function MapIllust({ color }) {
  return (
    <svg viewBox="0 0 280 160" className="h-full w-full" aria-hidden>
      {/* Soft map shape */}
      <path d="M 20 130 L 40 110 L 80 120 L 130 90 L 180 110 L 230 80 L 260 90 L 260 150 L 20 150 Z"
        fill={color} opacity="0.18" />
      {/* Route */}
      <path
        d="M 36 120 Q 80 70, 130 100 T 240 50"
        stroke={color}
        strokeWidth="2.5"
        fill="none"
        strokeDasharray="5 6"
        strokeLinecap="round"
      />
      {/* Start pin */}
      <circle cx="36" cy="120" r="8" fill={color} />
      <circle cx="36" cy="120" r="3" fill="#ffffff" />
      {/* End pin */}
      <path d="M 240 30 L 232 50 L 248 50 Z" fill={color} />
      <circle cx="240" cy="50" r="12" fill={color} />
      <circle cx="240" cy="50" r="4" fill="#ffffff" />
    </svg>
  );
}

function ShieldIllust({ color }) {
  return (
    <svg viewBox="0 0 280 160" className="h-full w-full" aria-hidden>
      <path
        d="M 140 24 L 96 42 L 96 84 Q 96 122 140 142 Q 184 122 184 84 L 184 42 Z"
        fill={color}
        opacity="0.92"
      />
      <path
        d="M 120 82 L 134 96 L 162 66"
        stroke="#ffffff"
        strokeWidth="5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ==================================================================
   Project data
   ================================================================== */

const projects = [
  {
    id: 'freecast',
    name: 'Freecast',
    logo: '/projects/freecast.png',
    LogoIcon: Network,
    accent: '#D9904C',
    bg: '#FFFFFF',
    blurb:
      'Built a content broadcasting and distribution platform that delivers high-quality streaming to audiences at scale.',
    Illust: NetworkIllust,
    image: '/Home/freecast.png',
  },
  {
    id: 'leadcenter',
    name: 'Leadcenter',
    logo: '/projects/leadcenter.svg',
    LogoIcon: Building2,
    accent: '#2E6CCC',
    bg: '#FFFFFF',
    blurb:
      'Engineered a lead intelligence and sales pipeline platform that helps teams qualify, route and close prospects faster.',
    Illust: ChartIllust,
    image: '/Home/lead.png',
  },
  {
    id: 'raisn',
    name: 'Raisn',
    logo: '/projects/raisn.png',
    LogoIcon: HeartPulse,
    accent: '#3FAA76',
    bg: '#FFFFFF',
    blurb:
      'Designed a capital-raising platform that connects founders with the right investors through structured deal flow.',
    Illust: PulseIllust,
    image: '/Home/Raisn.png',
  },
  {
    id: 'altcase',
    name: 'Altcase',
    logo: '/projects/altcase.png',
    LogoIcon: ShoppingBag,
    accent: '#D14F66',
    bg: '#FFFFFF',
    blurb:
      'Built an investment platform that makes wealth-building accessible. Real-time data, interactive dashboards, and a mobile-first PWA experience help novice and experienced investors discover low-risk, high-return assets with full regulatory compliance.',
    Illust: RetailIllust,
    image: '/Home/altcast.png',
  },
  {
    id: 'penomo',
    name: 'Penomo',
    logo: '/projects/penomo.png',
    LogoIcon: ShieldCheck,
    accent: '#7B5BB8',
    bg: '#FFFFFF',
    blurb:
      'Delivered a fintech infrastructure platform for transparent, low-friction capital flows in emerging markets.',
    Illust: ShieldIllust,
    image: '/Home/penomo.png',
  },
  {
    id: 'mhp',
    name: 'MHP',
    logo: '/projects/mhp.png',
    LogoIcon: Truck,
    accent: '#C19C30',
    bg: '#FFFFFF',
    blurb:
      'Built a digital platform for My Home Pathway that turns operational signals into clear, actionable workflows for the teams running the day-to-day.',
    Illust: MapIllust,
    image: '/Home/mhp.png',
  },
];

/* ==================================================================
   Section
   ================================================================== */

export default function Projects() {
  const router = useRouter();
  const total = projects.length;
  const [active, setActive] = useState(0);
  const [drag, setDrag] = useState(0);
  const [hover, setHover] = useState(false);

  // Phones use narrower cards on a tighter arc so the neighbours peek in
  // instead of overlapping the active card.
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const sync = () => setCompact(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Phones: the project chips scroll sideways. Keep the active chip in view
  // as the carousel auto-rotates (scroll the row only, never the page).
  const chipsRef = useRef(null);
  useEffect(() => {
    const row = chipsRef.current;
    if (!row || row.scrollWidth <= row.clientWidth) return;
    const chip = row.children[active];
    if (!chip) return;
    const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollTo({ left, behavior: 'smooth' });
  }, [active]);

  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const lastXRef = useRef(0);
  const cursorRef = useRef(null);
  const stackRef = useRef(null);
  const tappedRef = useRef(null);
  const startYRef = useRef(0);
  const lastYRef = useRef(0);

  const pauseRef = useRef(false);
  const pauseTimerRef = useRef(null);

  // Auto-rotate
  useEffect(() => {
    const id = setInterval(() => {
      if (!pauseRef.current && !draggingRef.current) {
        setActive(a => (a + 1) % total);
      }
    }, 5000);
    return () => clearInterval(id);
  }, [total]);

  const pauseAuto = (ms = 4500) => {
    pauseRef.current = true;
    clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => {
      pauseRef.current = false;
    }, ms);
  };

  const goTo = i => {
    setActive(i);
    pauseAuto();
  };
  const prev = () => {
    setActive(a => (a - 1 + total) % total);
    pauseAuto();
  };
  const next = () => {
    setActive(a => (a + 1) % total);
    pauseAuto();
  };

  /* Pointer handlers (drag + custom cursor positioning) */
  const onPointerDown = e => {
    draggingRef.current = true;
    startXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    startYRef.current = e.clientY;
    lastYRef.current = e.clientY;
    pauseRef.current = true;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {}
  };

  const onPointerMove = e => {
    if (cursorRef.current && stackRef.current) {
      const r = stackRef.current.getBoundingClientRect();
      cursorRef.current.style.transform = `translate(${e.clientX - r.left}px, ${e.clientY - r.top}px) translate(-50%, -50%)`;
    }
    if (draggingRef.current) {
      lastXRef.current = e.clientX;
      lastYRef.current = e.clientY;
      setDrag(e.clientX - startXRef.current);
    }
  };

  // `cancelled` is true when the gesture was a vertical page scroll (the browser
  // fires pointercancel once touch-pan-y scrolling takes over) or the pointer
  // left the stack — in those cases we must NOT treat it as a tap, so a scroll
  // on a card no longer redirects on touch.
  const finishDrag = (cancelled = false) => {
    if (!draggingRef.current) return;
    const dx = lastXRef.current - startXRef.current;
    const dy = lastYRef.current - startYRef.current;
    draggingRef.current = false;
    if (Math.abs(dx) > 80) {
      // Horizontal swipe — advance/retreat one card.
      if (dx > 0) setActive(a => (a - 1 + total) % total);
      else setActive(a => (a + 1) % total);
    } else if (
      !cancelled &&
      Math.abs(dx) < 6 &&
      Math.abs(dy) < 6 &&
      tappedRef.current !== null
    ) {
      // Deliberate tap (barely moved in X AND Y, and not a scroll): center card
      // -> open case study, side card -> bring it to the front. Handled here
      // because pointer capture steals the synthetic click event.
      const i = tappedRef.current;
      if (cardPos(i) === 0) {
        router.push(`/case-studies/${projects[i].id}`);
      } else if (Math.abs(cardPos(i)) <= 1) {
        goTo(i);
      }
    }
    tappedRef.current = null;
    setDrag(0);
    pauseAuto();
  };

  /* Card position with circular shortest-path */
  const cardPos = i => {
    let diff = i - active;
    const half = total / 2;
    if (diff > half) diff -= total;
    if (diff <= -half) diff += total;
    return diff;
  };

  /* Cards travel along an arc — sin/cos of their angle on a virtual circle.
     Only |pos| ≤ 1 is visible (3 cards max). */
  const cardStyle = i => {
    const p = cardPos(i);
    const visible = Math.abs(p) <= 1;

    // Virtual circle: side cards arc out and slightly down.
    const ANGLE_STEP = 26; // degrees per card position
    const RADIUS = compact ? 650 : 820; // px

    const angleDeg = p * ANGLE_STEP;
    const angleRad = (angleDeg * Math.PI) / 180;

    const x = RADIUS * Math.sin(angleRad) + drag;
    const y = RADIUS * (1 - Math.cos(angleRad));
    const rot = angleDeg;
    // All visible cards share the same scale so every card reads as the same
    // size on screen; the fan/scroll motion comes from position + rotation only.
    const scale = visible ? 1 : 0.7;
    const opacity = visible ? 1 - Math.abs(p) * 0.15 : 0;

    return {
      transform: `translate(-50%, -50%) translateX(${x}px) translateY(${y}px) rotate(${rot}deg) scale(${scale})`,
      opacity,
      zIndex: 100 - Math.abs(p),
      transition: draggingRef.current
        ? 'none'
        : 'transform 0.8s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.45s ease',
      pointerEvents: visible ? 'auto' : 'none',
    };
  };

  return (
    <section data-nav-theme="light" className="relative z-0 overflow-hidden bg-[#f6f8fb] py-24 lg:py-32">
      {/* Soft brand glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#058BF0]/8 blur-[120px]"
      />

      <div className="relative mx-auto w-11/12 max-w-[1280px] text-center">
        {/* Heading */}
        <h2 className="mx-auto max-w-5xl font-poppins text-3xl font-bold leading-[1.2] sm:leading-[1.2] lg:leading-[1.2] tracking-tight text-[#0a1b3a] sm:text-4xl lg:text-5xl xl:text-[3.5rem]">
          Projects that turn ideas into production-grade products
        </h2>

        {/* Body */}
        <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-gray-600 lg:text-lg">
          We partner with leaders across media, fintech, cloud and cybersecurity, engineering AI-first products that create measurable business impact.
        </p>

        {/* Tabs */}
        {/* Phones: arrows pinned left/right, chips scroll sideways between
            them. From sm up the chip wrapper is display:contents, so the
            row wraps and centres exactly as before. */}
        <div className="mt-10 flex items-center gap-2 sm:flex-wrap sm:justify-center sm:gap-3">
          <button
            type="button"
            onClick={prev}
            aria-label="Previous project"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-[#0a1b3a] transition hover:border-[#0a1b3a] hover:bg-gray-50"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2.2} />
          </button>

          <div
            ref={chipsRef}
            className="flex min-w-0 flex-1 snap-x items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:contents"
          >

          {projects.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => goTo(i)}
              className={`shrink-0 snap-start whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition sm:px-5 sm:text-sm ${
                i === active
                  ? 'bg-[#0a1b3a] text-white'
                  : 'border border-gray-300 bg-white text-[#0a1b3a] hover:border-[#0a1b3a] hover:bg-gray-50'
              }`}
            >
              {p.name}
            </button>
          ))}

          {/* View all — navigates to the full case-studies page */}
          <a
            href="/case-studies"
            className="group inline-flex shrink-0 snap-start items-center gap-1.5 whitespace-nowrap rounded-full border border-transparent px-4 py-2 text-xs font-semibold text-gray-500 transition hover:border-gray-300 hover:bg-gray-50 hover:text-[#0a1b3a] sm:px-5 sm:text-sm"
          >
            View all
            <ArrowUpRight
              className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              strokeWidth={2.2}
            />
          </a>
          </div>

          <button
            type="button"
            onClick={next}
            aria-label="Next project"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-[#0a1b3a] transition hover:border-[#0a1b3a] hover:bg-gray-50"
          >
            <ChevronRight className="h-4 w-4" strokeWidth={2.2} />
          </button>
        </div>

        {/* Card stack */}
        <div
          ref={stackRef}
          className="relative h-[470px] touch-pan-y select-none sm:h-[600px]"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={() => finishDrag(false)}
          onPointerCancel={() => finishDrag(true)}
          onMouseLeave={() => finishDrag(true)}
        >
          {projects.map((p, i) => {
            const { LogoIcon, Illust } = p;
            return (
              <article
                key={p.id}
                className="absolute left-1/2 top-1/2 flex w-[280px] flex-col cursor-none overflow-hidden rounded-3xl p-5 text-left shadow-[0_28px_60px_-18px_rgba(10,27,58,0.28)] sm:w-[340px] sm:p-7"
                style={{ ...cardStyle(i), backgroundColor: p.bg }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onPointerDown={() => {
                  // Record which card the press started on; finishDrag turns a
                  // no-move press into a navigation / focus action.
                  tappedRef.current = i;
                }}
              >
                {/* Logo mark — real brand wordmark when available,
                    colored Lucide chip as fallback. */}
                {p.logo ? (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={p.logo}
                    alt={`${p.name} logo`}
                    loading="lazy"
                    decoding="async"
                    className="h-7 w-auto max-w-[130px] object-contain sm:h-9 sm:max-w-[150px]"
                    draggable={false}
                  />
                ) : (
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-2xl"
                    style={{ backgroundColor: p.accent }}
                  >
                    <LogoIcon className="h-5 w-5 text-white" strokeWidth={2.4} />
                  </div>
                )}

                {/* Body — name is conveyed by the logo wordmark above.
                    Clamped to 3 lines so every card keeps the same height
                    regardless of how long the individual blurb is. Done with
                    inline styles because this repo's Tailwind (3.2.6) has no
                    line-clamp utility. */}
                <p
                  className="mt-5 text-sm leading-relaxed text-[#0a1b3a]/70"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    // Reserve exactly 4 lines so the image sits at the same
                    // spot on every card, even when a blurb is shorter.
                    minHeight: '5.7rem',
                  }}
                >
                  {p.blurb}
                </p>

                {/* Project preview image (falls back to the SVG illustration).
                    Fixed height + small gap so it aligns on every card. */}
                <div className="relative mt-4 h-[170px] w-full shrink-0 overflow-hidden rounded-2xl bg-[#f6f8fb]">
                  {p.image ? (
                    <Image
                      src={p.image}
                      alt={`${p.name} preview`}
                      fill
                      sizes="(max-width: 639px) 280px, 340px"
                      className="object-cover"
                      draggable={false}
                    />
                  ) : (
                    <div className="p-4">
                      <Illust color={p.accent} />
                    </div>
                  )}
                </div>
              </article>
            );
          })}

          {/* Custom cursor decoration */}
          <div
            ref={cursorRef}
            className={`pointer-events-none absolute left-0 top-0 z-[200] flex items-center gap-2 rounded-full bg-[#0a1b3a] px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-[0_10px_28px_rgba(10,27,58,0.35)] transition-opacity duration-200 ${
              hover ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ willChange: 'transform' }}
          >
            View case study
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.6} />
          </div>
        </div>
      </div>
    </section>
  );
}
